import React from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  Input,
  Button,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@tonic/shared/components/ui';
import { Search, MoreVertical, Calendar } from 'lucide-react';
import { formatCurrency, formatDate } from '@/utils/format';
import { DataTableWrapper } from '@/features/shared/components/data-display/DataTableWrapper';

/**
 * @typedef {Object} RepairItem
 * @property {*} id
 * @property {*} customer
 * @property {*} product
 * @property {*} status
 * @property {*} dateReceived
 * @property {*} estimatedCompletion
 * @property {*} technician
 */

// Mock repair data
const mockRepairs = [
  {
    id: "REP-BILLY001",
    customer: {
      name: "John Smith",
      email: "john.smith@email.com"
    },
    product: {
      id: "BILLY-80263832",
      name: "BILLY Bookcase",
      category: "Storage"
    },
    status: "in_progress",
    priority: "high",
    technician: "Mike Wilson",
    requestDate: "2024-03-15",
    estimatedCompletion: "2024-03-20",
    issue: "Damaged hinges and loose shelves",
    notes: "Customer reported wobbling shelves"
  },
  {
    id: "REP-POANG002",
    customer: {
      name: "Emma Johnson",
      email: "emma.j@email.com"
    },
    product: {
      id: "POANG-89129308",
      name: "POÄNG Armchair",
      category: "Seating"
    },
    status: "pending",
    priority: "medium",
    technician: "Sarah Brown",
    requestDate: "2024-03-14",
    estimatedCompletion: "2024-03-19",
    issue: "Fabric tear and frame wobble",
    notes: "Initial assessment needed"
  },
  {
    id: "REP-MALM003",
    customer: {
      name: "Michael Brown",
      email: "m.brown@email.com"
    },
    product: {
      id: "MALM-89129307",
      name: "MALM Bed Frame",
      category: "Bedroom"
    },
    status: "completed",
    priority: "low",
    technician: "Tom Davis",
    requestDate: "2024-03-13",
    estimatedCompletion: "2024-03-18",
    issue: "Missing parts and scratches",
    notes: "Parts ordered and received"
  }
];

const dateRanges = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '180d', label: 'Last 180 days' },
  { value: '365d', label: 'Last 365 days' },
];

const repairStatuses = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const columns = [
  {
    accessorKey: 'id',
    header: 'Repair ID',
    cell: ({ row }) => (
      <Link
        to={`/admin/re-commerce/repairs/${row.original.id}`}
        className="font-medium text-primary hover:underline"
      >
        {row.original.id}
      </Link>
    ),
  },
  {
    accessorKey: 'customer',
    header: 'Customer',
    cell: ({ row }) => (
      <div>
        <div className="font-medium">{row.original.customer.name}</div>
        <div className="text-sm text-muted-foreground">{row.original.customer.email}</div>
      </div>
    ),
  },
  {
    accessorKey: 'product',
    header: 'Product',
    cell: ({ row }) => (
      <div>
        <div className="font-medium">{row.original.product.name}</div>
        <div className="text-sm text-muted-foreground">{row.original.product.category}</div>
      </div>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.original.status.toLowerCase();
      const variant = {
        pending: 'outline',
        in_progress: 'secondary',
        completed: 'default',
        cancelled: 'destructive',
      }[status] || 'outline';

      return (
        <Badge variant={variant}>
          {row.original.status.replace('_', ' ').toUpperCase()}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'priority',
    header: 'Priority',
    cell: ({ row }) => {
      const priority = row.original.priority.toLowerCase();
      const variant = {
        high: 'destructive',
        medium: 'secondary',
        low: 'outline'
      }[priority] || 'outline';

      return (
        <Badge variant={variant}>
          {row.original.priority.toUpperCase()}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'technician',
    header: 'Technician',
    cell: ({ row }) => row.original.technician,
  },
  {
    accessorKey: 'requestDate',
    header: 'Request Date',
    cell: ({ row }) => formatDate(row.original.requestDate),
  },
  {
    accessorKey: 'actions',
    header: '',
    cell: ({ row }) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem>View Details</DropdownMenuItem>
          <DropdownMenuItem>Update Status</DropdownMenuItem>
          <DropdownMenuItem>Assign Technician</DropdownMenuItem>
          <DropdownMenuItem>Contact Customer</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  },
];

export function RepairListPage() {
  const [filters, setFilters] = React.useState({
    search: '',
    status: 'all',
    dateRange: '30d',
  });

  // Filter repairs based on current filters
  const filteredRepairs = React.useMemo(() => {
    return mockRepairs.filter(repair => {
      const matchesSearch =
        repair.id.toLowerCase().includes(filters.search.toLowerCase()) ||
        repair.customer.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        repair.product.name.toLowerCase().includes(filters.search.toLowerCase());

      const matchesStatus =
        filters.status === 'all' ||
        repair.status === filters.status;

      return matchesSearch && matchesStatus;
    });
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <div className="p-6">
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div className="relative flex-1 w-full md:max-w-sm">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-muted-foreground" />
              </div>
              <Input
                placeholder="Search repairs..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-9 w-full"
              />
            </div>

            <div className="flex gap-4 items-center w-full md:w-auto">
              <Select
                value={filters.status}
                onValueChange={(value) => handleFilterChange('status', value)}
              >
                <SelectTrigger className="w-full md:w-[200px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  {repairStatuses.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-muted-foreground" />
                <Select
                  value={filters.dateRange}
                  onValueChange={(value) => handleFilterChange('dateRange', value)}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {dateRanges.map((range) => (
                      <SelectItem key={range.value} value={range.value}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <DataTableWrapper
            columns={columns}
            data={filteredRepairs}
            emptyMessage="No repairs found"
            loadingMessage="Loading repairs..."
          />
        </CardContent>
      </Card>
    </div>
  );
}
